<template>
  <div class="absolute top-3 right-3 z-20">
    <button
      class="comment-button flex items-center justify-center flex-shrink-0 cursor-pointer rounded-full font-medium transition-all duration-200 w-9 h-9"
      :class="{
        'has-comments': threadCount > 0 && !hasAssignedThreads,
        'has-assigned': hasAssignedThreads,
        'has-unresolved': unresolvedCount > 0 && !hasAssignedThreads,
      }"
      :title="getButtonTitle()"
      @click="toggleComments"
    >
      <CIcon icon="comment" :size="16" />
      <span v-if="threadCount > 0" class="comment-count text-xs font-bold ml-0.5">{{ threadCount }}</span>

      <!-- Notification badges -->
      <span
        v-if="hasAssignedThreads"
        class="notification-badge assigned-badge absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-xs font-bold px-1"
        :title="`${assignedCount} thread${assignedCount !== 1 ? 's' : ''} assigned to you`"
      >
        <CIcon icon="user" :size="10" />
      </span>
      <span
        v-else-if="unresolvedCount > 0"
        class="notification-badge unresolved-badge absolute w-3 h-3 min-w-[12px] -right-0.5 -top-0.5 rounded-full font-black"
        :title="`${unresolvedCount} unresolved thread${unresolvedCount !== 1 ? 's' : ''}`"
      >
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="showComments"
        class="comments-overlay fixed inset-0 flex justify-center items-center z-[9999]"
        @click="closeComments"
      >
        <div
          class="comments-panel bg-white rounded-2xl w-4/5 h-4/5 max-h-[700px] flex flex-col overflow-hidden"
          @click.stop
        >
          <div class="absolute top-4 right-4 z-[1]">
            <button
              class="close-button w-8 h-8 rounded-full p-0 cursor-pointer flex items-center justify-center transition-all duration-200"
              @click="closeComments"
            >
              <CIcon icon="times" :size="16" />
            </button>
          </div>
          <SimpleComments
            :canvas-id="props.canvasId"
            :component-id="props.componentId"
            :current-user="currentUserData"
            @user-activity="markUserActive"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import SimpleComments from './SimpleComments.vue'
import { useSharedCommentsStorage } from '@lib/composables/useSharedCommentsStorage'
import { usePitcherApi, useAdmin } from '@lib/sdk/main'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

interface Props {
  componentId: string
  canvasId?: string
}

const props = defineProps<Props>()

const themeVars = useThemeVars()

const showComments = ref(false)
const currentUserId = ref<string | null>(null)
const currentUserName = ref<string | null>(null)
const currentUserData = ref<any>(null)

// Use shared backend storage for threads
const canvasIdRef = toRef(props, 'canvasId')
const { threads, loadComments } = useSharedCommentsStorage(canvasIdRef)

// Filter threads for current component
const componentThreads = computed(() => {
  return threads.value.filter((thread) => !props.componentId || thread.componentId === props.componentId)
})

// Count unresolved threads
const unresolvedCount = computed(() => {
  return componentThreads.value.filter((thread) => !thread.resolved).length
})

// Check if user has assigned threads
const hasAssignedThreads = computed(() => {
  if (!currentUserId.value) return false
  return componentThreads.value.some((thread) => {
    if (thread.resolved) return false
    return thread.assignee?.value === currentUserId.value || thread.assignee?.value === Number(currentUserId.value)
  })
})

// Count of assigned threads
const assignedCount = computed(() => {
  if (!currentUserId.value) return 0

  return componentThreads.value.filter((thread) => {
    if (thread.resolved) return false
    return thread.assignee?.value === currentUserId.value || thread.assignee?.value === Number(currentUserId.value)
  }).length
})

// Total thread count for display
const threadCount = computed(() => componentThreads.value.length)

// Auto-refresh comments for real-time updates
const POLLING_INTERVAL = 5000 // 5 seconds
const USER_ACTIVITY_TIMEOUT = 3000 // 3 seconds

// Track user activity
const isUserActive = ref(false)
const userActivityTimer = ref<NodeJS.Timeout | null>(null)

// Mark user as active and set a timeout to mark as inactive
const markUserActive = () => {
  isUserActive.value = true

  // Clear existing timer
  if (userActivityTimer.value) {
    clearTimeout(userActivityTimer.value)
  }

  // Set new timer to mark inactive after timeout
  userActivityTimer.value = setTimeout(() => {
    isUserActive.value = false
  }, USER_ACTIVITY_TIMEOUT)
}

// Determine when to poll
const shouldPoll = computed(() => {
  // Don't poll if user is actively typing/interacting
  if (isUserActive.value) return false

  // Poll when comments panel is open
  if (showComments.value) return true

  // Poll when there are existing threads (to track updates)
  if (threadCount.value > 0) return true

  return false
})

// Set up polling interval
const { pause, resume } = useIntervalFn(() => {
  if (props.canvasId) {
    // Use silent loading for periodic updates
    loadComments(true)
  }
}, POLLING_INTERVAL)

// Watch polling conditions and control interval
watch(
  shouldPoll,
  (should) => {
    if (should) {
      resume()
    } else {
      pause()
    }
  },
  { immediate: true },
)

// Fetch current user data
const fetchCurrentUser = async () => {
  try {
    let api: any = usePitcherApi()
    if (window.location.href.indexOf('/admin/') !== -1) {
      api = useAdmin()
    }
    const env = await api.getEnv()

    currentUserId.value = String(env.pitcher.user.id)
    currentUserName.value = env.pitcher.user.name || env.pitcher.user.email || null
    currentUserData.value = {
      id: env.pitcher.user.id,
      name: env.pitcher.user.name || env.pitcher.user.email,
      email: env.pitcher.user.email,
    }
  } catch (error) {
    console.warn('CommentButton: Failed to get user info', error)
  }
}

// Initialize on mount
fetchCurrentUser()

const getButtonTitle = () => {
  if (hasAssignedThreads.value) {
    return `${assignedCount.value} thread${assignedCount.value !== 1 ? 's' : ''} assigned to you`
  } else if (unresolvedCount.value > 0) {
    return `${unresolvedCount.value} unresolved comment${unresolvedCount.value !== 1 ? 's' : ''}`
  } else if (threadCount.value > 0) {
    return `${threadCount.value} comment${threadCount.value !== 1 ? 's' : ''}`
  }
  return 'No comments'
}

const toggleComments = () => {
  showComments.value = !showComments.value

  // Refresh comments when opening the panel
  if (showComments.value && props.canvasId) {
    loadComments()
  }
}

const closeComments = () => {
  showComments.value = false
}
</script>

<style scoped lang="scss">
.comment-button {
  background-color: v-bind('themeVars.primary6');
  border: 1px solid v-bind('themeVars.primary5');
  color: v-bind('themeVars.primary');

  &.has-comments,
  &.has-unresolved {
    background: v-bind('themeVars.primary');
    color: white;
    border-color: v-bind('themeVars.primary');

    &:hover {
      background: v-bind('themeVars.primary');
      border-color: v-bind('themeVars.primary');
    }
  }

  &.has-assigned {
    background: v-bind('themeVars.success');
    color: white;
    border-color: v-bind('themeVars.success');
    animation: pulse-green 2s infinite;

    &:hover {
      background: v-bind('themeVars.success');
      border-color: v-bind('themeVars.success');
      animation: none;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

.comment-count {
  font-size: 10px;
  font-weight: 700;
  margin-left: 2px;
}

.notification-badge {
  &.assigned-badge {
    background: v-bind('themeVars.success');
    color: white;
    animation: bounce 1.5s infinite;
  }

  &.unresolved-badge {
    background: v-bind('themeVars.error');
    color: white;
    font-weight: 900;
  }
}

.comments-overlay {
  background: rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.2s ease;
}

.comments-panel {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.close-button {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #64748b;
  display: none;

  &:hover {
    background: #ffffff;
    color: #334155;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
